import { Box, Chip, Grid, LinearProgress, Stack, Typography } from '@mui/material';
import { compact, map, sum, take } from 'lodash';
import React from 'react';

import { ExternalSlideTransferJob } from 'interfaces/job';
import { formatDateTimeForUser } from 'utils/dateTime';
import { JobDetailCard, JobDetailSection, JobDetailValue } from './JobDetailCard';

const MAX_VISIBLE_SOURCE_PATHS = 3;

const getProgress = (job: ExternalSlideTransferJob) => {
  const results = job.results;
  const processedFileCount = sum([
    results?.transferredFileCount || 0,
    results?.skippedFileCount || 0,
    results?.failedFileCount || 0,
  ]);
  const totalFileCount = job.params.transferCount ?? processedFileCount;

  return {
    processedFileCount,
    totalFileCount,
    percentage: totalFileCount ? Math.min(100, (processedFileCount / totalFileCount) * 100) : 0,
  };
};

export const ExternalSlideTransferJobDetailsSection: React.FC<{ job: ExternalSlideTransferJob }> = ({ job }) => {
  const { params, results } = job;
  const { processedFileCount, totalFileCount, percentage } = getProgress(job);
  const sourcePaths = params.sourcePaths ?? [];
  const visibleSourcePaths = take(sourcePaths, MAX_VISIBLE_SOURCE_PATHS);
  const hiddenSourcePathCount = sourcePaths.length - visibleSourcePaths.length;
  const lastProgressAt = formatDateTimeForUser(results?.lastProgressAt);
  const runtimeDetails = compact([
    results?.taskArn && `Task ARN: ${results.taskArn}`,
    results?.taskLogUrl && `Task log: ${results.taskLogUrl}`,
  ]);

  return (
    <Grid item>
      <JobDetailCard>
        <Grid item>
          <Typography variant="h4">External Slide Transfer</Typography>
        </Grid>

        <JobDetailSection label="Progress">
          <Stack spacing={0.75}>
            <Typography variant="body2">
              {processedFileCount.toLocaleString()} of {totalFileCount.toLocaleString()} files processed
            </Typography>
            <LinearProgress variant="determinate" value={percentage} aria-label="External slide transfer progress" />
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <Chip size="small" label={`${(results?.transferredFileCount || 0).toLocaleString()} transferred`} />
              <Chip size="small" label={`${(results?.skippedFileCount || 0).toLocaleString()} skipped`} />
              <Chip
                size="small"
                color={results?.failedFileCount ? 'error' : 'default'}
                label={`${(results?.failedFileCount || 0).toLocaleString()} failed`}
              />
            </Stack>
          </Stack>
        </JobDetailSection>

        <JobDetailSection label="Source">
          <Stack spacing={0.75}>
            <JobDetailValue>
              {params.providerName} / {params.bucket}
            </JobDetailValue>
            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
              {map(visibleSourcePaths, (sourcePath) => (
                <Chip key={sourcePath} size="small" variant="outlined" label={sourcePath} />
              ))}
              {hiddenSourcePathCount > 0 && <Chip size="small" label={`+${hiddenSourcePathCount} more`} />}
            </Stack>
          </Stack>
        </JobDetailSection>

        <JobDetailSection label="Destination">
          <JobDetailValue>{params.destinationPrefix || 'Not available'}</JobDetailValue>
        </JobDetailSection>

        <JobDetailSection label="Worker batches">
          <JobDetailValue>
            {params.workloadCount.toLocaleString()} batches, up to {params.transferBatchSize.toLocaleString()} files per
            worker
          </JobDetailValue>
        </JobDetailSection>

        <Grid item container spacing={2}>
          <Grid item xs={12} sm={6}>
            <JobDetailSection label="Conflict policy">
              <JobDetailValue>{params.destinationConflictPolicy}</JobDetailValue>
            </JobDetailSection>
          </Grid>
          <Grid item xs={12} sm={6}>
            <JobDetailSection label="Last heartbeat / progress">
              <JobDetailValue>{lastProgressAt || 'Not reported yet'}</JobDetailValue>
            </JobDetailSection>
          </Grid>
        </Grid>

        {results?.transferredFilesCsvArtifactId && (
          <JobDetailSection label="Transferred-files CSV">
            <JobDetailValue>{results.transferredFilesCsvArtifactId}</JobDetailValue>
          </JobDetailSection>
        )}

        {runtimeDetails.length > 0 && (
          <JobDetailSection label="Runtime details">
            <Stack spacing={0.5}>
              {map(runtimeDetails, (runtimeDetail) => (
                <Box key={runtimeDetail} sx={{ overflowWrap: 'anywhere' }}>
                  <Typography variant="body2">{runtimeDetail}</Typography>
                </Box>
              ))}
            </Stack>
          </JobDetailSection>
        )}
      </JobDetailCard>
    </Grid>
  );
};
